import React, { useState, useEffect } from 'react';
import theme from '../../../theme/globalTheme';
import { Table, Card, DatePicker, Input, message, Result, Spin ,Button} from 'antd';
import { supabase } from '../../../api/supabase';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

const { RangePicker } = DatePicker;
const { Search } = Input;

const PartnerLunchOrdersView = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [dateRange, setDateRange] = useState([]);
  const [searchText, setSearchText] = useState('');
  const navigate = useNavigate();
  
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    sortField: 'date_of_charter',
    sortDirection: 'descend'
  });

  useEffect(() => {
    verifyAccess();
  }, []);

  useEffect(() => {
    if (isAuthorized) {
      if (dateRange.length === 0) {
        const startOfMonth = dayjs().startOf('month');
        const endOfMonth = dayjs().endOf('month');
        setDateRange([startOfMonth, endOfMonth]);
      }
      fetchOrders();
    }
  }, [isAuthorized, pagination.current, pagination.sortField, pagination.sortDirection, dateRange, searchText]);

  const verifyAccess = async () => {
    try {
      const token = new URLSearchParams(window.location.search).get('token');
      if (!token) {
        setIsAuthorized(false);
        setAuthChecking(false);
        return;
      }

      const { data, error } = await supabase
        .from('partner_access')
        .select('*')
        .eq('access_token', token)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        setIsAuthorized(false);
        message.error('Token invalide ou expiré');
      } else if (data.expires_at && dayjs(data.expires_at).isBefore(dayjs())) {
        setIsAuthorized(false);
        message.error('Accès expiré');
      } else {
        setIsAuthorized(true);
      }
    } catch (error) {
      setIsAuthorized(false);
      message.error('Erreur d\'authentification');
    } finally {
      setAuthChecking(false);
    }
  };

  const fetchOrders = async () => {
    if (!isAuthorized) return;
    
    setLoading(true);
    try {
      let query = supabase
        .from('lunch_orders')
        .select('*', { count: 'exact' });

      if (dateRange.length === 2) {
        query = query
          .gte('date_of_charter', dateRange[0].format('YYYY-MM-DD'))
          .lte('date_of_charter', dateRange[1].format('YYYY-MM-DD'));
      }

      if (searchText) {
        query = query.or(`
          client_name.ilike.%${searchText}%,
          charterer_name.ilike.%${searchText}%,
          description_title.ilike.%${searchText}%
        `);
      }

      const from = (pagination.current - 1) * pagination.pageSize;
      const to = from + pagination.pageSize - 1;
      
      query = query
        .range(from, to)
        .order(pagination.sortField, { ascending: pagination.sortDirection === 'ascend' });

      const { data, count, error } = await query;

      if (error) throw error;

      setOrders(data);
      setPagination(prev => ({ ...prev, total: count }));
    } catch (error) {
      message.error('Erreur lors de la récupération des commandes');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Date de la sortie',
      dataIndex: 'date_of_charter',
      key: 'date_of_charter',
      render: (date) => dayjs(date).format('DD/MM/YYYY'),
      sorter: true,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Heure de prise en charge',
      dataIndex: 'pick_up_time',
      key: 'pick_up_time',
      render: (time) => dayjs(time, 'HH:mm:ss').format('HH:mm'),
      sorter: true,
    },
    {
      title: 'Nom du client',
      dataIndex: 'client_name',
      key: 'client_name',
      sorter: true,
    },
    {
      title: 'Description',
      dataIndex: 'description_title',
      key: 'description_title',
    },
    {
      title: 'Agence/Hôtel',
      dataIndex: 'charterer_name',
      key: 'charterer_name',
      sorter: true,
    }
  ];

  if (authChecking) {
    return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgDark, padding: '20px' }} className="h-screen flex items-center justify-center">
        <Spin size="large" tip="Vérification de l'accès..." />
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <Result
        status="403"
        title="Accès non autorisé"
        subTitle="Désolé, vous n'êtes pas autorisé à accéder à cette page."
        extra={
          <Button type="primary" onClick={() => navigate('/')}>
            Retour à l'accueil
          </Button>
        }
      />
    );
  }

  return (
    <div className="p-6">
      <Card className="mb-6">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <h1 className="text-2xl font-bold">Commandes de déjeuner à venir</h1>
          <div className="flex gap-4">
            <RangePicker 
              value={dateRange}
              onChange={setDateRange}
              format="DD/MM/YYYY"
            />
            <Search
              placeholder="Rechercher..."
              allowClear
              onSearch={setSearchText}
              style={{ width: 200 }}
            />
          </div>
        </div>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize }));
            }
          }}
          onChange={(pagination, filters, sorter) => {
            setPagination(prev => ({
              ...prev,
              sortField: sorter.field,
              sortDirection: sorter.order
            }));
          }}
        />
      </Card>
    </div>
  );
};

export default PartnerLunchOrdersView;